import { clearAuth, getRefreshToken, getToken, setAccessToken, setRefreshToken } from './auth';

type TokenRefreshResponse = {
  access?: string;
  refresh?: string;
};

let refreshInFlight: Promise<string | null> | null = null;

function ensureTrailingSlash(value: string): string {
  return value.endsWith('/') ? value : `${value}/`;
}

export function getApiBaseUrl(): string {
  const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL;
  if (configuredBaseUrl) {
    return ensureTrailingSlash(configuredBaseUrl);
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:8000/api/v1/';
    }
  }

  return '/api/v1/';
}

function buildAuthHeaders(options: RequestInit, token: string): Headers {
  const isFormData = options.body instanceof FormData;
  const headers = new Headers(options.headers);

  if (!isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return headers;
}

function redirectToLogin() {
  clearAuth();
  if (typeof window !== 'undefined') {
    window.location.assign('/login');
  }
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  if (!refreshInFlight) {
    refreshInFlight = fetch(`${getApiBaseUrl()}auth/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    })
      .then(async (response) => {
        if (!response.ok) {
          return null;
        }

        const data = (await response.json()) as TokenRefreshResponse;
        if (!data.access) {
          return null;
        }

        if (data.refresh) {
          setRefreshToken(data.refresh);
        }
        setAccessToken(data.access);
        return data.access;
      })
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
}

async function requestWithToken(url: string, options: RequestInit, token: string): Promise<Response> {
  return fetch(url, { ...options, headers: buildAuthHeaders(options, token) });
}

export async function fetchAuth(path: string, options: RequestInit = {}) {
  const token = getToken();
  const url = path.startsWith('http') ? path : `${getApiBaseUrl()}${path.replace(/^\/+/, '')}`;
  const response = await requestWithToken(url, options, token);

  if (response.status === 401) {
    const refreshedToken = await refreshAccessToken();
    if (refreshedToken) {
      const retryResponse = await requestWithToken(url, options, refreshedToken);
      if (retryResponse.status !== 401) {
        return retryResponse;
      }
    }

    redirectToLogin();
    throw new Error('Sessão expirada');
  }

  return response;
}
